import { useState, useCallback, useRef } from "react";
import { useDisclosure, useToast, Text } from "@chakra-ui/react";
import SkeletonBase from "newui/components/Skeleton";
import { InfoBox } from "newui/components/IconBox/InfoBox";
import { Button } from "newui/components/Button";
import Modal from "newui/components/ui/Modal/Modal";
import { useHasAIEvaluationAccess } from "hooks/featureFlags";
import { useEvaluationPanel } from "../../../hooks/useEvaluationPanel";
import type { PanelMode } from "../../../types/panel.type";
import { PanelModeSelector } from "./PanelModeSelector";
import { PanelMemberTable } from "./PanelMemberTable";
import { AddMemberForm } from "./AddMemberForm";
import { InviteConfirmationDrawer } from "../../InviteConfirmationDrawer";
import { useCurrentCompany } from "hooks/auth";
import { Rfx } from "newui/features/Rfx/types/rfx.types";
import { WarningBox } from "newui/components/IconBox";

interface EvaluationPanelStepProps {
	rfxData: Rfx;
	isLoading?: boolean;
	isRfxLead?: boolean;
	savedPanelMode?: PanelMode;
	onPanelModeChange?: (mode: PanelMode) => void;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
}

export const EvaluationPanelStep: React.FC<EvaluationPanelStepProps> = ({
	rfxData,
	isLoading = false,
	isRfxLead = false,
	savedPanelMode,
	onPanelModeChange,
	onBeforeNext,
}) => {
	const rfxId = rfxData?.id;
	const hasAIAccess = useHasAIEvaluationAccess();
	const { id: companyId, name: companyName } = useCurrentCompany();
	const rfxTitle = rfxData?.name ?? "";
	const toast = useToast();

	const {
		panelMembers,
		panelMode,
		isLoading: isPanelLoading,
		error,
		hasInvitedMembers,
		hasPendingMembers,
		addMember,
		removeMember,
		sendInvitations,
		resendInvitations,
		setMode,
	} = useEvaluationPanel({ rfxId, initialMode: savedPanelMode });

	const {
		isOpen: isDrawerOpen,
		onOpen: onDrawerOpen,
		onClose: onDrawerClose,
	} = useDisclosure();

	const [selectedResendIds, setSelectedResendIds] = useState<number[]>([]);
	const [isResending, setIsResending] = useState(false);
	const [showPendingWarning, setShowPendingWarning] = useState(false);
	const pendingResolveRef = useRef<((proceed: boolean) => void) | null>(null);

	const handleModeChange = useCallback(
		(mode: PanelMode) => {
			setMode(mode);
			if (onPanelModeChange) {
				onPanelModeChange(mode);
			}
		},
		[setMode, onPanelModeChange]
	);

	const handleToggleResend = useCallback((memberId: number) => {
		setSelectedResendIds((prev) => {
			if (prev.includes(memberId)) {
				return prev.filter((id) => id !== memberId);
			}
			return [...prev, memberId];
		});
	}, []);

	const handleResend = useCallback(async () => {
		if (selectedResendIds.length === 0) {
			return;
		}
		setIsResending(true);
		try {
			const result = await resendInvitations(selectedResendIds);
			toast({
				title: "Invitations resent",
				description: `${result.sent} invitation(s) resent successfully.`,
				status: "success",
				duration: 4000,
				isClosable: true,
			});
			setSelectedResendIds([]);
		} catch {
			toast({
				title: "Error",
				description: "Failed to resend invitations. Please try again.",
				status: "error",
				duration: 4000,
				isClosable: true,
			});
		} finally {
			setIsResending(false);
		}
	}, [selectedResendIds, resendInvitations, toast]);

	const pendingMemberNames = panelMembers
		.filter((m) => m.status === "pending")
		.map((m) => m.name || m.email);

	const checkBeforeNext = useCallback(async (): Promise<boolean> => {
		const isPanelMode = panelMode === "manual_panel" || panelMode === "ai_panel";
		if (isPanelMode && panelMembers.length === 0) {
			toast({
				title: "No Panel Members",
				description: "Please invite Evaluation panel or select 'score myself' to continue.",
				status: "warning",
				duration: 5000,
				isClosable: true,
			});
			return false;
		}
		if (!hasPendingMembers) {
			return true;
		}
		return new Promise<boolean>((resolve) => {
			pendingResolveRef.current = resolve;
			setShowPendingWarning(true);
		});
	}, [hasPendingMembers, panelMode, panelMembers.length, toast]);

	const handleSendAndContinue = () => {
		setShowPendingWarning(false);
		if (pendingResolveRef.current) {
			pendingResolveRef.current(false);
			pendingResolveRef.current = null;
		}
		onDrawerOpen();
	};

	const handleWarningContinue = () => {
		setShowPendingWarning(false);
		if (pendingResolveRef.current) {
			pendingResolveRef.current(true);
			pendingResolveRef.current = null;
		}
	};

	const handleWarningCancel = () => {
		setShowPendingWarning(false);
		if (pendingResolveRef.current) {
			pendingResolveRef.current(false);
			pendingResolveRef.current = null;
		}
	};

	// Register the before-next check with parent
	if (onBeforeNext) {
		onBeforeNext(checkBeforeNext);
	}

	if (isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
			</div>
		);
	}

	const showMemberManagement = panelMode === "manual_panel" || panelMode === "ai_panel";

	return (
		<div data-testid="evaluation-panel-step">
			{isRfxLead && (
				<InfoBox className="mb-6">
					<p className="pl-6 text-sm text-gray-600">
						The Evaluation Panel allows you to define who will be scoring supplier
						submissions. You can choose to evaluate submissions yourself, invite
						colleagues or external experts to join the panel, or use AI-powered
						evaluation.
					</p>
				</InfoBox>
			)}

			<PanelModeSelector
				selectedMode={panelMode}
				hasAIAccess={hasAIAccess}
				isRfxLead={isRfxLead}
				onSelectMode={handleModeChange}
				disabled={!isRfxLead || hasInvitedMembers}
			/>

			{showMemberManagement && (
				<div className="space-y-6">
					<PanelMemberTable
						members={panelMembers}
						onRemove={isRfxLead ? removeMember : undefined}
						selectedResendIds={isRfxLead ? selectedResendIds : []}
						onToggleResend={isRfxLead ? handleToggleResend : undefined}
						hasInvitedMembers={isRfxLead && hasInvitedMembers}
					/>
					{isRfxLead && showMemberManagement && (
						<div className="mt-6 flex items-center justify-between">
							<div>
								{hasInvitedMembers && (
									<Button
										btntype="secondary"
										onClick={handleResend}
										disabled={selectedResendIds.length === 0 || isResending}
										isLoading={isResending}
										data-testid="resend-invitations-btn"
									>
										Resend Invitations ({selectedResendIds.length})
									</Button>
								)}
							</div>
							<Button
								btntype="primary"
								onClick={onDrawerOpen}
								disabled={!hasPendingMembers}
								data-testid="proceed-invitations-btn"
							>
								Send Invitations
							</Button>
						</div>
					)}
					{isRfxLead && (
						<AddMemberForm
							companyId={companyId}
							panelMode={panelMode}
							rfxId={rfxId}
							panelMembers={panelMembers}
							onAddMember={addMember}
						/>
					)}
				</div>
			)}

			{error && (
				<p className="mt-3 text-sm text-red-600" data-testid="panel-error">
					{error}
				</p>
			)}

			{showMemberManagement && (
				<InviteConfirmationDrawer
					isOpen={isDrawerOpen}
					onClose={onDrawerClose}
					rfxId={rfxId}
					panelMembers={panelMembers}
					onSendInvitations={sendInvitations}
					companyName={companyName}
					rfxTitle={rfxTitle}
				/>
			)}

			<Modal
				isOpen={showPendingWarning}
				onClose={handleWarningCancel}
				title="Unsent Invitations"
				iconType="warning"
				size="md"
				closeOnEsc
				closeOnOverlayClick
				customFooter={
					<div className="flex gap-2">
						<Button btntype="primary" onClick={handleSendAndContinue}>
							Send Invites
						</Button>
						<Button btntype="secondary" onClick={handleWarningContinue}>
							Continue without sending
						</Button>
						<Button btntype="secondary" onClick={handleWarningCancel}>
							Cancel
						</Button>
					</div>
				}
			>
				<Text color="gray.600">
					The following panel members have not been sent their invitations yet:
				</Text>
				<ul className="list-disc pl-5 text-sm text-gray-700">
					{pendingMemberNames.map((name) => (
						<li key={name}>{name}</li>
					))}
				</ul>
				<WarningBox>
					<div className="mb-2">
						They will not be able to participate in the evaluation until
						invitations are sent.
					</div>
					<div>Are you sure you want to continue?</div>
				</WarningBox>
			</Modal>
		</div>
	);
};
